/* eslint-disable max-len */
import { useInfiniteQuery } from '@tanstack/react-query';
import { remixApi } from '@tectonic/api-client';
import {
  ElemasonWidgetActionType,
  NavigationActionType,
} from '@tectonic/types';
import { sum } from 'lodash-es';
import { type FC } from 'react';
import { ProductReviews } from '../../../components';
import {
  useElemasonAnalyticsContext,
  useElemasonPageContext,
} from '../../../contexts';
import { useAccessorValue } from '../../../hooks/useAccessorValue';
import { queryKeys } from '../../../queryKeys';

import type {
  ElemasonProductReviewsWidget,
  ProductReview,
} from '@tectonic/types';

interface ElemasonProductReviewsWidgetProps {
  widget: ElemasonProductReviewsWidget;
}

const ProductReviewsWidget: FC<ElemasonProductReviewsWidgetProps> = ({
  widget: { data, config },
}) => {
  const slug = useAccessorValue(data?.slug || {});

  if (!slug) {
    return null;
  }

  const { dispatch } = useElemasonPageContext();
  const analyticsContext = useElemasonAnalyticsContext();

  const viewAllReviews = () => {
    dispatch(
      {
        type: ElemasonWidgetActionType.NAVIGATE_TO,
        payload: {
          type: NavigationActionType.PRODUCT_REVIEWS,
          route: {
            slug,
          },
        },
      },
      analyticsContext
    );
  };

  const pp = data?.reviewsPerPage ?? 3;

  // TODO written amaeturish code + brain fog need to polish this better and handle errors, loading states
  // TODO check if we need to set the pagination params in page url

  const {
    data: reviewsData,
    isLoading,
    isError,
    fetchNextPage: fetchNextReviews,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: queryKeys.reviewsForProduct(slug),
    initialPageParam: 1,
    queryFn: async ({ pageParam }) =>
      remixApi.getReviewsForProduct(
        slug,
        new URLSearchParams({
          perPage: pp.toString(),
          page: pageParam.toString(),
        })
      ),
    getNextPageParam: (lastPage, allPages, _lastPageParam) => {
      const allPagesCount = sum(
        allPages.map((pageData) => pageData.hits.length)
      );
      if (allPagesCount >= lastPage.found) {
        return undefined;
      }
      return lastPage.page + 1;
    },
  });

  const { stats: ratingsDetails, found } = reviewsData?.pages[0] || {};

  if (
    !ratingsDetails ||
    !ratingsDetails?.default ||
    ratingsDetails.default.count === 0 ||
    isError
  ) {
    return null;
  }

  let hits: ProductReview[] = [];
  reviewsData?.pages.forEach((pageData) => {
    hits = hits.concat(pageData.hits);
  });

  const saneRatingIcon = data?.ratingIcon || 'outline-star';

  return (
    <div className="flex flex-col">
      <ProductReviews
        icon={saneRatingIcon}
        config={config}
        data={hits}
        total={found}
        title={data?.title}
        getNext={
          data?.paginationType === 'NEXT_PAGE'
            ? fetchNextReviews
            : viewAllReviews
        }
        hasNextPage={hasNextPage}
        isLoading={isFetchingNextPage || isLoading}
        isError={isError}
        paginationButtonData={data?.paginateButtonData}
      />
    </div>
  );
};

export { ProductReviewsWidget };
